import { useEffect, useMemo, useState } from "react";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { debounce } from "lodash";
import {
  useConsolidationPriorities,
  useConsolidationPriorityStatuses,
} from "@/hooks/useConsolidation";
import { ConsolidationPriority } from "@/models/consolidation";
import { MODE_LABELS } from "../consolidationPriorityDisplay";

interface UsePriorityTableDataReturn {
  data: ConsolidationPriority[];
  search: string;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: (filters: ColumnFiltersState) => void;
  handleSearchChange: (searchValue: string) => void;
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  /** Distinct requester names across the whole (unfiltered) list. */
  requesterOptions: string[];
}

const searchableText = (priority: ConsolidationPriority): string =>
  [
    priority.lawTitle,
    priority.lawExternalId,
    priority.pivotExternalId,
    priority.requestedByName,
    MODE_LABELS[priority.mode],
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/**
 * Faceted-filter matchers per column id (values come from the toolbar's
 * DataTableFacetedFilter as arrays of option values).
 */
const FILTER_MATCHERS: Record<
  string,
  (priority: ConsolidationPriority, values: string[]) => boolean
> = {
  mode: (p, values) => values.includes(p.mode),
  level: (p, values) => values.includes(String(p.level)),
  requestedByName: (p, values) => values.includes(p.requestedByName ?? ""),
};

const matchesColumnFilters = (
  priority: ConsolidationPriority,
  columnFilters: ColumnFiltersState
): boolean =>
  columnFilters.every((filter) => {
    const matcher = FILTER_MATCHERS[filter.id];
    const values = Array.isArray(filter.value)
      ? (filter.value as string[])
      : [];
    if (!matcher || values.length === 0) return true;
    return matcher(priority, values);
  });

const SORT_ACCESSORS: Record<
  string,
  (priority: ConsolidationPriority) => string | number
> = {
  lawTitle: (p) => (p.lawTitle ?? p.lawExternalId).toLowerCase(),
  mode: (p) => MODE_LABELS[p.mode],
  level: (p) => p.level,
  requestedByName: (p) => (p.requestedByName ?? "").toLowerCase(),
  createdAt: (p) => {
    const time = new Date(p.createdAt).getTime();
    return Number.isNaN(time) ? 0 : time;
  },
};

/**
 * Table state for the priorities list — the same debounced-search / sorting /
 * pagination interface the tone-of-voice settings table uses, applied
 * client-side: the priorities endpoint returns the whole (small,
 * admin-curated) list, which useConsolidationPriorities already refetches
 * every 30s for live status badges.
 */
const usePriorityTableData = (): UsePriorityTableDataReturn => {
  const prioritiesQuery = useConsolidationPriorities();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);

  const [columnFilters, setColumnFiltersState] = useState<ColumnFiltersState>(
    []
  );
  const setColumnFilters = (filters: ColumnFiltersState) => {
    setPageIndex(0);
    setColumnFiltersState(filters);
  };

  // Immediate input vs debounced applied search, like useToneOfVoiceTableData.
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const debouncedApply = debounce((searchValue: string) => {
      setPageIndex(0);
      setSearch(searchValue);
    }, 300);
    debouncedApply(input);
    return () => {
      debouncedApply.cancel();
    };
  }, [input]);

  const filtered = useMemo(() => {
    const all = prioritiesQuery.data ?? [];
    const query = search.trim().toLowerCase();
    const matched = all.filter(
      (priority) =>
        matchesColumnFilters(priority, columnFilters) &&
        (!query || searchableText(priority).includes(query))
    );

    const sort = sorting[0];
    const accessor = sort ? SORT_ACCESSORS[sort.id] : undefined;
    if (!sort || !accessor) return matched;

    return [...matched].sort((a, b) => {
      const aValue = accessor(a);
      const bValue = accessor(b);
      const cmp = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sort.desc ? -cmp : cmp;
    });
  }, [prioritiesQuery.data, search, sorting, columnFilters]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const clampedPageIndex = Math.min(pageIndex, pageCount - 1);
  const pageRows = useMemo(
    () =>
      filtered.slice(
        clampedPageIndex * pageSize,
        (clampedPageIndex + 1) * pageSize
      ),
    [filtered, clampedPageIndex, pageSize]
  );

  // The status rollup is expensive server-side, so it is fetched only for the
  // visible page and merged in. status stays undefined while it loads (the
  // column shows a placeholder), and becomes the rollup or null once fetched.
  const pageIds = useMemo(
    () => pageRows.map((priority) => priority.id),
    [pageRows]
  );
  const statusesQuery = useConsolidationPriorityStatuses(pageIds);
  const data = useMemo(() => {
    const statusById = new Map(
      (statusesQuery.data ?? []).map((item) => [item.priorityId, item.status])
    );
    return pageRows.map((priority) => ({
      ...priority,
      status: statusesQuery.data
        ? (statusById.get(priority.id) ?? null)
        : undefined,
    }));
  }, [pageRows, statusesQuery.data]);

  const requesterOptions = useMemo(() => {
    const names = new Set<string>();
    for (const priority of prioritiesQuery.data ?? []) {
      if (priority.requestedByName) names.add(priority.requestedByName);
    }
    return [...names].sort((a, b) => a.localeCompare(b, "da"));
  }, [prioritiesQuery.data]);

  return {
    data,
    search: input,
    pageCount,
    pageIndex: clampedPageIndex,
    pageSize,
    sorting,
    columnFilters,
    setPageIndex,
    setPageSize,
    setSorting,
    setColumnFilters,
    handleSearchChange: setInput,
    isLoading: prioritiesQuery.isLoading,
    isError: prioritiesQuery.isError,
    totalCount: filtered.length,
    requesterOptions,
  };
};

export default usePriorityTableData;
